import  { useState, useEffect } from "react";
import FleetrifyCard from "../../components/Card";
import DashboardLayout from "../../components/DashboardLayout";
import ServiceFilter from "../../components/pages/Services/modal/ServiceFilter";
import { ChevronDown, Plus } from "lucide-react";
import AddServiceModal from "../../components/pages/Services/modal/AddServiceModal";
import PlanUsage, { PLAN_FULL_TITLE, usePlanUsage } from "../../components/PlanUsage";
import ServicesTable, {
  ServiceData,
} from "../../components/Table/ServicesTable";
import { ColumnConfig } from "../../utils/exportUtils";
import {
  useGetAllServicesListQuery,
  useLazyGetAllServicesListQuery,
  useGetAllServicesForSummaryQuery,
  // useGetServiceByIdQuery,
  Service,
} from "../../redux/services/serviceApi";
import {
  useGetCompanyInfoQuery,
} from "../../redux/services/settingsApi";
import ServicesSummary from "../../components/pages/Services/modal/ServiceSummary";

const Services = () => {
  const [showAddServiceModal, setShowAddServiceModal] = useState(false);
  const { isFull: servicesFull } = usePlanUsage("services");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [exportData, setExportData] = useState<{
    rows: ServiceData[];
    columns: ColumnConfig[];
  }>({
    rows: [],
    columns: [],
  });
  // get company info
    const { data: companyInfoData } = useGetCompanyInfoQuery();
    const companyId = companyInfoData?.data?.companyInfo?.id;

  const {
    data: servicesData,
    isLoading: isTableLoading,
    isFetching: isTableFetching,
    refetch: refetchTable,
  } = useGetAllServicesListQuery(
    {
      companyId: companyId || "",
      page: currentPage,
      per_page: 10,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const [triggerFetchPage] = useLazyGetAllServicesListQuery();
  const [shouldFetchAll] = useState(false);

  const [allServices, setAllServices] = useState<Service[]>([]);
  const [isFetchingAllServices, setIsFetchingAllServices] = useState(true);

  const {
    data: summaryData,
    refetch: refetchSummary,
  } = useGetAllServicesForSummaryQuery({ companyId: companyId || "" });


  useEffect(() => {
    const fetchAllServices = async () => {
      //  Do nothing unless user asked
      if (!shouldFetchAll) {
        setAllServices(summaryData?.data ?? servicesData?.data ?? []);
        setIsFetchingAllServices(false);
        return;
      }

      if (!servicesData?.meta) return;

      setIsFetchingAllServices(true);

      try {
        const totalPages = servicesData.meta.last_page;

        const pages = Array.from({ length: totalPages }, (_, i) =>
          triggerFetchPage({
            page: i + 1, per_page: 10,
            companyId: companyId || "",
          }).unwrap()
        );

        const responses = await Promise.all(pages);
        setAllServices(responses.flatMap((r) => r.data ?? []));
      } catch (error) {
        console.error("Error fetching all services:", error);
        setAllServices([]);
      } finally {
        setIsFetchingAllServices(false);
      }
    };

    fetchAllServices();
  }, [
    servicesData?.data,
    servicesData?.meta,
    shouldFetchAll,
    servicesData?.meta?.last_page,
    summaryData?.data,
    triggerFetchPage,
  ]);

  const refetch = () => {
    refetchTable();
    refetchSummary();
  };

  useEffect(() => {
    if (search) {
      setCurrentPage(1);
    }
  }, [search]);

  const services = servicesData?.data || [];
  const meta = servicesData?.meta;
  console.log("servicesData", servicesData);

  return (
    <DashboardLayout
      title="Services"
      description="Get a clear, actionable view of your staff and operations in one place. Track key metrics, and export the data you need to make faster, smarter decisions every day."
      containerClass="pb-10"
      showDateRange={true}
      onDataRefresh={refetch}
    >
      {isTableLoading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading Services data...</p>
          </div>
        </div>
      ) : (
        <>
        <div className="flex flex-wrap justify-end items-center gap-3 mt-8">
          <PlanUsage resource="services" />
          <button
            onClick={() => setShowAddServiceModal(true)}
            disabled={servicesFull}
            title={servicesFull ? PLAN_FULL_TITLE : undefined}
            className="rounded-lg bg-[#143887] text-white px-4 py-2 text-sm font-medium flex items-center gap-2 shadow-sm hover:bg-[#0f2d6b] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="h-4 w-4" />
            Add New Service
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>

        <ServicesSummary
          services={allServices}
          isLoading={isFetchingAllServices}
          onFilterService={(serviceName: string) => {
            setSearch(serviceName);
            setCurrentPage(1);
          }}
        />

        <FleetrifyCard cardContainerClass="mt-10">
          <div className="px-0 py-0 md:px-6 md:py-6">
            <ServiceFilter onSearch={setSearch} exportData={exportData} />
          </div>

          <ServicesTable
            services={services}
            isLoading={isTableLoading || isTableFetching}
            search={search}
            onExportData={setExportData}
            currentPage={meta?.current_page || 1}
            lastPage={meta?.last_page || 1}
            totalItems={meta?.total || 0}
            pageSize={meta?.per_page || 10}
            onPageChange={setCurrentPage}
          />
        </FleetrifyCard>
      </>
      )}

      {showAddServiceModal && (
        <AddServiceModal
          isModalOpen={showAddServiceModal}
          setIsModalOpen={setShowAddServiceModal}
          onSuccess={refetch}
        />
      )}
    </DashboardLayout>
  );
};

export default Services;
